import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Container, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useDispatch } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import DrishtiPageHeader from '../../components/drishtiPageHeader';
import { useAppSelector } from '../../app/hooks';
import { formatStation } from '../../constants/stationConfig';
import {
    CALIBRATION_STATUS,
    completeCalibration,
    isTerminalCalibrationStatus,
    pollCalibrationStatus,
    RETRY_CAP,
    startCalibration,
    type CalibrationStatusResponse,
    type CompleteCalibrationResponse,
    type ErrorResponse,
    type StartCalibrationResponse,
} from '../../services/drishtiHandler';
import { EventName, EventType, logDrishtiMetricEvent } from '../../services/eventService';
import {
    beginFinalizePhase,
    calibrationCompleted,
    calibrationErrorCleared,
    calibrationPollError,
    calibrationReset,
    calibrationStarted,
    startNewStation,
    statusUpdated,
} from '../../slice/drishtiInstallConfigSlice';

// One poll per minute. Matches the design's POLL_INTERVAL_MS.
export const POLL_INTERVAL_MS = 60_000;

export const MAX_OUTCOME_RECORD_ATTEMPTS = 3;

// Per-attempt timeout: if calibration hasn't reached a terminal state within 10 minutes,
// the frontend synthesizes a FAILED status so the installer can retry or escalate.
// 3-camera setups get 15 minutes due to longer OTA update times; all others get 10 minutes.
export const ATTEMPT_TIMEOUT_3_CAM_MS = 15 * 60 * 1000;
export const ATTEMPT_TIMEOUT_DEFAULT_MS = 10 * 60 * 1000;

const attemptElapsedMs = (attemptStartedAtMs: number | null, upstreamCreatedAt?: string): number | undefined => {
    const anchorMs = attemptStartedAtMs ?? Date.parse(upstreamCreatedAt ?? '');
    return anchorMs ? Date.now() - anchorMs : undefined;
};

// mm:ss formatter for the progress-screen elapsed timer.
const formatElapsed = (elapsedMs: number): string => {
    const totalSeconds = Math.floor(elapsedMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
};

const DrishtiCalibration = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { deviceSerial, site, stationNumber, stationConfig, calibration } = useAppSelector(
        (state) => state.drishtiInstallConfig,
    );
    const status = calibration.status;
    const phase = calibration.phase;
    // Nothing has started yet when phase is null, and the first run is always the dry run.
    const activePhase = phase ?? 'test';
    // An ESCALATED resume is the one case the phase is unknown, so its events leave the field out.
    const loggedPhase = phase ?? (status === null ? 'test' : undefined);
    const remediation = calibration.failure?.remediation ?? [];
    const attemptTimeoutMs = stationConfig.cameraCount === 3 ? ATTEMPT_TIMEOUT_3_CAM_MS : ATTEMPT_TIMEOUT_DEFAULT_MS;
    const attemptCount = calibration.attemptCount;

    // Guards a single mount from firing startCalibration twice (React strict mode + the
    // effect re-running when status transitions to IN_PROGRESS both risk a double fire).
    const startFiredRef = useRef(false);
    // Guards completeCalibration from firing more than once per terminal status. The poll
    // interval may tick again before Redux updates status to terminal and the effect cleans
    // up — without this ref, a second poll would call completeCalibration again and overwrite
    // the real escalation response with the idempotent no-op ({escalated: false}).
    const terminalRecordedRef = useRef(false);
    // Separate from terminalRecordedRef, which reopens on a failed record; the event logs once per run.
    const terminalLoggedRef = useRef(false);
    const recordFailuresRef = useRef(0);
    const recordRequestedAtMsRef = useRef(0);
    const pollCountRef = useRef(0);
    const lastErrorRef = useRef(calibration.lastError);
    lastErrorRef.current = calibration.lastError;
    const startedAtRef = useRef(calibration.currentAttemptStartedAtMs);
    startedAtRef.current = calibration.currentAttemptStartedAtMs;
    const calibrationIdRef = useRef(calibration.calibrationId);
    calibrationIdRef.current = calibration.calibrationId;
    // Session-local on purpose: a refresh before the response arrives returns the installer to the
    // idle screen rather than firing a second run in the background.
    const [startAttempts, setStartAttempts] = useState(0);

    // Elapsed-time tracking for the progress screen. The start timestamp lives on the slice
    // (calibrationStarted stamps it), so a refresh mid-run resumes the counter from the
    // original request start rather than restarting from zero. Only the 1-second re-render
    // tick is component-local.
    const currentAttemptStartedAtMs = calibration.currentAttemptStartedAtMs;
    const [progressElapsedMs, setProgressElapsedMs] = useState(() =>
        currentAttemptStartedAtMs !== null ? Date.now() - currentAttemptStartedAtMs : 0,
    );
    useEffect(() => {
        if (status !== CALIBRATION_STATUS.IN_PROGRESS || currentAttemptStartedAtMs === null) {
            return;
        }
        setProgressElapsedMs(Date.now() - currentAttemptStartedAtMs);
        const id = setInterval(() => {
            setProgressElapsedMs(Date.now() - currentAttemptStartedAtMs);
        }, 1000);
        return () => clearInterval(id);
    }, [status, currentAttemptStartedAtMs]);

    // Both phases report the same statuses, so a failure only means something with the phase beside it.
    const logCalibration = (eventName: EventName, eventType: EventType, miscEventData: {}, durationMs?: number) =>
        logDrishtiMetricEvent({
            eventName,
            eventType,
            deviceId: deviceSerial ?? undefined,
            duration_ms: durationMs,
            miscEventData: { phase: loggedPhase, ...miscEventData },
        });

    // A resumed failure carries a status but no reason, so one poll fetches the remediation. Only
    // for a FAILED seed: a SUCCEEDED one is a live station and moving it backwards would offer
    // Retry on a calibrated device, and an escalated one shows no remediation to fetch.
    const needsRemediation = useRef(
        status === CALIBRATION_STATUS.FAILED && calibration.failure === null && !calibration.escalated,
    );
    const [isFetchingRemediation, setIsFetchingRemediation] = useState(needsRemediation.current);
    useEffect(() => {
        if (!needsRemediation.current || !deviceSerial) {
            return;
        }
        needsRemediation.current = false;
        // No completeCalibration here: the outcome is already recorded, which is what the register
        // response read to report FAILED.
        pollCalibrationStatus(deviceSerial, (success, result) => {
            setIsFetchingRemediation(false);
            if (!success) {
                const message = (result as ErrorResponse).message ?? 'Poll error';
                // Marked `resumed`: this is the remediation fetch, not the run's own polling loop.
                logCalibration(EventName.DRISHTI_CALIBRATION_STATUS_POLL, EventType.FAILURE, {
                    error: message,
                    resumed: true,
                });
                dispatch(calibrationPollError(message));
                return;
            }
            // Fill in the reason only, never move the status. The upstream calibration service
            // tracks its own run and commonly still reports IN_PROGRESS for a device asset
            // management has recorded as failed; taking that would swap the failure screen for a
            // progress screen and poll a run that is already decided.
            const pollResult = result as CalibrationStatusResponse;
            if (pollResult.status === CALIBRATION_STATUS.FAILED) {
                dispatch(statusUpdated(pollResult));
            }
        });
    }, [deviceSerial, dispatch]);

    const onCalibrate = () => {
        startFiredRef.current = false;
        dispatch(calibrationErrorCleared());
        setStartAttempts((attempts) => attempts + 1);
    };

    const onRetry = () => {
        terminalRecordedRef.current = false;
        terminalLoggedRef.current = false;
        recordFailuresRef.current = 0;
        dispatch(calibrationReset());
        onCalibrate();
    };

    const onStartNewStation = () => {
        // Only a run that never succeeded is abandoned; from the success screen this is "Next station".
        if (status !== CALIBRATION_STATUS.SUCCEEDED) {
            logCalibration(EventName.DRISHTI_CALIBRATION_ABANDONED, EventType.COUNT, {
                status,
                attemptCount,
                escalated: calibration.escalated,
            });
        }
        dispatch(startNewStation());
        navigate('/drishti/station');
    };

    const onFinalize = () => {
        terminalRecordedRef.current = false;
        // The finalize run is a second run; without this reset its outcome would never be logged.
        terminalLoggedRef.current = false;
        recordFailuresRef.current = 0;
        startFiredRef.current = false;
        dispatch(beginFinalizePhase());
        setStartAttempts((attempts) => attempts + 1);
    };

    useEffect(() => {
        // Precondition guard is redundant with the <Navigate> below — this is just a
        // defensive short-circuit so the effect body never runs with missing state.
        if (!deviceSerial || !site || !stationNumber) {
            return;
        }

        // Bootstrap: the installer has tapped Calibrate, so kick off the run on the backend.
        // Also gated on the absence of a prior transport error: redux-persist keeps `lastError`
        // across a refresh, so without this check a persisted start-time failure would auto-fire
        // a new request in the background while the UI showed the "Try again" alert.
        if (status === null && startAttempts > 0 && !startFiredRef.current && !lastErrorRef.current) {
            startFiredRef.current = true;
            pollCountRef.current = 0;
            const startRequestedAtMs = Date.now();
            logCalibration(EventName.DRISHTI_CALIBRATION_STARTED, EventType.START, {
                site,
                stationNumber,
                // The slice increments on the response, so the run being started is the next attempt.
                attemptCount: attemptCount + 1,
            });
            startCalibration(
                {
                    deviceId: deviceSerial,
                    site,
                    stationNumber,
                    dryRun: activePhase === 'test',
                },
                (success, result) => {
                    if (success) {
                        const started = result as StartCalibrationResponse;
                        logCalibration(
                            EventName.DRISHTI_CALIBRATION_STARTED,
                            EventType.SUCCESS,
                            {
                                calibrationWorkflow: started.calibrationWorkflow,
                                status: started.status,
                                attemptCount: attemptCount + 1,
                            },
                            Date.now() - startRequestedAtMs,
                        );
                        dispatch(calibrationStarted(started, activePhase));
                    } else {
                        const message = (result as ErrorResponse).message ?? 'Failed to start';
                        // Calibration never began, so this is the START's own outcome, not a failed run.
                        logCalibration(
                            EventName.DRISHTI_CALIBRATION_STARTED,
                            EventType.FAILURE,
                            { error: message },
                            Date.now() - startRequestedAtMs,
                        );
                        dispatch(calibrationPollError(message));
                    }
                },
            );
            return;
        }

        // Terminal state: nothing more to poll. Cleanup below runs on the next status
        // transition or unmount to clear any interval that was set.
        if (status !== null && isTerminalCalibrationStatus(status)) {
            return;
        }

        // Start hasn't succeeded yet — don't poll until we have a status.
        if (status === null) {
            return;
        }

        // The run's outcome, logged once per attempt with the elapsed time it took to get there.
        const logTerminal = (terminal: CalibrationStatusResponse) => {
            if (terminalLoggedRef.current) {
                return;
            }
            terminalLoggedRef.current = true;
            const durationMs = attemptElapsedMs(startedAtRef.current, terminal.createdAt);
            const context = { calibrationId: terminal.calibrationId, attemptCount };
            if (terminal.status === CALIBRATION_STATUS.SUCCEEDED) {
                logCalibration(EventName.DRISHTI_CALIBRATION_COMPLETED, EventType.SUCCESS, context, durationMs);
                return;
            }
            logCalibration(
                EventName.DRISHTI_CALIBRATION_FAILED,
                EventType.FAILURE,
                {
                    ...context,
                    code: terminal.failure?.code,
                    calibrationRetryable: terminal.failure?.calibrationRetryable,
                },
                durationMs,
            );
        };

        // Shared by both terminal paths: record the outcome in Leo, and log the escalation it decided on.
        // Returns whether the terminal status may now be shown — false while the escalation decision is
        // still outstanding, so the installer never sees a Retry that the next response takes away.
        const applyCompletion = (
            outcome: 'APPLIED' | 'FAILED',
            completeSuccess: boolean,
            completeResult: CompleteCalibrationResponse | ErrorResponse,
        ) => {
            const recordDurationMs = Date.now() - recordRequestedAtMsRef.current;
            if (!completeSuccess) {
                recordFailuresRef.current += 1;
                // Worth its own event: the run finished, but the lifecycle state lags the installer's screen.
                logCalibration(
                    EventName.DRISHTI_CALIBRATION_OUTCOME_RECORDED,
                    EventType.FAILURE,
                    {
                        outcome,
                        error: (completeResult as ErrorResponse).message,
                        recordAttempts: recordFailuresRef.current,
                    },
                    recordDurationMs,
                );
                const givingUp = recordFailuresRef.current >= MAX_OUTCOME_RECORD_ATTEMPTS;
                // Reopened so the next poll tick retries the record; left closed once we give up on it.
                terminalRecordedRef.current = givingUp;
                return givingUp;
            }
            const completed = completeResult as CompleteCalibrationResponse;
            logCalibration(
                EventName.DRISHTI_CALIBRATION_OUTCOME_RECORDED,
                EventType.SUCCESS,
                {
                    outcome,
                    escalated: completed.escalated,
                    failedAttemptCount: completed.failedAttemptCount,
                    recordAttempts: recordFailuresRef.current + 1,
                },
                recordDurationMs,
            );
            dispatch(calibrationCompleted(completed));
            if (completed.escalated) {
                logCalibration(EventName.DRISHTI_CALIBRATION_ESCALATED, EventType.COUNT, {
                    failedAttemptCount: completed.failedAttemptCount,
                });
            }
            return true;
        };

        // Non-terminal + non-null: poll immediately, then on a fixed interval.
        // Each poll checks the attempt timeout first.
        const pollOnce = () => {
            const startedAtMs = startedAtRef.current;
            if (startedAtMs && Date.now() - startedAtMs >= attemptTimeoutMs) {
                const timeoutStatus: CalibrationStatusResponse = {
                    calibrationId: calibrationIdRef.current ?? '',
                    deviceId: deviceSerial,
                    status: CALIBRATION_STATUS.FAILED,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    failure: {
                        code: 'FRONTEND_TIMEOUT',
                        message: 'Calibration did not complete within the allowed time.',
                        calibrationRetryable: false,
                        remediation: [{ instruction: 'Retry calibration. If this persists, contact engineering.' }],
                    },
                };
                // Record the failed attempt with the backend so Leo bumps failedAttemptCount
                // and can decide whether to escalate. Guarded by terminalRecordedRef so the
                // interval can't fire this a second time before status transitions.
                logTerminal(timeoutStatus);
                if (!terminalRecordedRef.current) {
                    terminalRecordedRef.current = true;
                    recordRequestedAtMsRef.current = Date.now();
                    completeCalibration(
                        deviceSerial,
                        'FAILED',
                        activePhase === 'test',
                        (completeSuccess, completeResult) => {
                            if (applyCompletion('FAILED', completeSuccess, completeResult)) {
                                dispatch(statusUpdated(timeoutStatus));
                            }
                        },
                    );
                }
                return;
            }
            pollCalibrationStatus(deviceSerial, (success, result) => {
                if (success) {
                    const pollResult = result as CalibrationStatusResponse;
                    if (isTerminalCalibrationStatus(pollResult.status)) {
                        logTerminal(pollResult);
                        // The in-flight record owns this run's terminal status. Landing it here as well
                        // would show a Retry that the escalation response then takes away.
                        if (terminalRecordedRef.current) {
                            return;
                        }
                        terminalRecordedRef.current = true;
                        // Terminal — record outcome in Leo and wait for escalation decision.
                        const outcome = pollResult.status === CALIBRATION_STATUS.SUCCEEDED ? 'APPLIED' : 'FAILED';
                        recordRequestedAtMsRef.current = Date.now();
                        completeCalibration(
                            deviceSerial,
                            outcome,
                            activePhase === 'test',
                            (completeSuccess, completeResult) => {
                                if (applyCompletion(outcome, completeSuccess, completeResult)) {
                                    dispatch(statusUpdated(pollResult));
                                }
                            },
                        );
                    } else {
                        pollCountRef.current += 1;
                        logCalibration(
                            EventName.DRISHTI_CALIBRATION_STATUS_POLL,
                            EventType.SUCCESS,
                            {
                                status: pollResult.status,
                                calibrationId: pollResult.calibrationId,
                                attemptCount,
                                pollCount: pollCountRef.current,
                            },
                            attemptElapsedMs(startedAtRef.current, pollResult.createdAt),
                        );
                        dispatch(statusUpdated(pollResult));
                    }
                } else {
                    const message = (result as ErrorResponse).message ?? 'Poll error';
                    logCalibration(EventName.DRISHTI_CALIBRATION_STATUS_POLL, EventType.FAILURE, { error: message });
                    dispatch(calibrationPollError(message));
                }
            });
        };
        pollOnce();
        const id = setInterval(pollOnce, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [status, deviceSerial, site, stationNumber, dispatch, startAttempts, phase]);

    // ---- Sub-screens ----

    // Precondition failure: send the installer back to config instead of rendering a broken
    // calibration screen. Also protects direct-URL navigation without state.
    if (!deviceSerial || !site || !stationNumber) {
        return <Navigate to="/drishti/config" replace />;
    }

    // Idle: nothing is asked of the backend until the installer taps Calibrate, so arriving from the
    // guide never starts a run behind their back.
    if (status === null && startAttempts === 0 && !calibration.lastError) {
        return (
            <Container>
                <DrishtiPageHeader
                    title="Calibration"
                    description={`Test marker placement for ${deviceSerial} at ${formatStation({
                        site,
                        stationNumber,
                    })}.`}
                />
                <Button variant="primary" onClick={onCalibrate}>
                    Test calibration
                </Button>
            </Container>
        );
    }

    // Starting: Calibrate has been tapped, no startCalibration response yet.
    if (status === null) {
        return (
            <Container>
                <DrishtiPageHeader title="Calibration" description="Getting ready to calibrate this device." />
                {calibration.lastError ? (
                    <SpaceBetween size="m">
                        <Alert type="error">Could not start calibration. Please try again.</Alert>
                        <Button onClick={onCalibrate}>Try again</Button>
                    </SpaceBetween>
                ) : (
                    <SpaceBetween direction="horizontal" size="s">
                        <Spinner />
                        <Box>Starting calibration…</Box>
                    </SpaceBetween>
                )}
            </Container>
        );
    }

    // Progress: status is non-terminal (IN_PROGRESS, UNKNOWN, or any future value the upstream
    // adds). Elapsed timer reassures the installer that the workflow is still running.
    if (!isTerminalCalibrationStatus(status)) {
        // A resumed run has no start time until its first poll answers, and 0m 00s for a run that is
        // minutes old reads worse than no timer at all.
        const elapsed = currentAttemptStartedAtMs === null ? '' : ` (${formatElapsed(progressElapsedMs)})`;
        return (
            <Container>
                <DrishtiPageHeader
                    title="Calibration"
                    description="Calibration is running. Keep this page open until it finishes."
                />
                <SpaceBetween direction="horizontal" size="s">
                    <Spinner />
                    <Box>Calibration in progress…{elapsed}</Box>
                </SpaceBetween>
            </Container>
        );
    }

    // Success: SUCCEEDED terminal.
    if (status === CALIBRATION_STATUS.SUCCEEDED) {
        // Test phase success: markers are positioned correctly, installer should affix permanently.
        if (phase === 'test') {
            return (
                <Container>
                    <DrishtiPageHeader
                        title="Test passed"
                        description="Markers are positioned correctly. Affix markers permanently, then re-run to finalize calibration."
                    />
                    <SpaceBetween size="m">
                        <Box variant="awsui-key-label">Site</Box>
                        <Box>{site}</Box>
                        <Box variant="awsui-key-label">Workstation</Box>
                        <Box>{stationNumber}</Box>
                        <Box variant="awsui-key-label">Device</Box>
                        <Box>{deviceSerial}</Box>
                        <Button variant="primary" onClick={onFinalize}>
                            Finalize calibration
                        </Button>
                    </SpaceBetween>
                </Container>
            );
        }

        // Finalize phase success: station is live.
        return (
            <Container>
                <DrishtiPageHeader title="Calibration complete" description="Station is live." />
                <SpaceBetween size="m">
                    {calibration.completedBeforeResume ? (
                        <Alert type="success" header="This workstation is already done">
                            Nothing left to do here — please move on to the next station.
                        </Alert>
                    ) : null}
                    <Box variant="awsui-key-label">Site</Box>
                    <Box>{site}</Box>
                    <Box variant="awsui-key-label">Workstation</Box>
                    <Box>{stationNumber}</Box>
                    <Box variant="awsui-key-label">Device</Box>
                    <Box>{deviceSerial}</Box>
                    {calibration.updatedAt ? (
                        <>
                            <Box variant="awsui-key-label">Completed</Box>
                            <Box>{new Date(calibration.updatedAt).toLocaleString()}</Box>
                        </>
                    ) : null}
                    <Button variant="primary" onClick={onStartNewStation}>
                        Next station
                    </Button>
                </SpaceBetween>
            </Container>
        );
    }

    // Failure terminal: one screen for every FAILED response. The failure code and
    // calibrationRetryable flag are stored in Redux for logging but not read here — the
    // installer-facing content is entirely driven by the backend-authored remediation list,
    // and every failure offers a Retry (escalation after the retry cap is a follow-up
    // decision pending the backend contract for who counts attempts).
    if (status === CALIBRATION_STATUS.FAILED) {
        if (calibration.escalated || attemptCount >= RETRY_CAP) {
            return (
                <Container>
                    <DrishtiPageHeader
                        title="Calibration escalated"
                        description="Engineering has been notified. You can move on to the next station."
                    />
                    <SpaceBetween size="m">
                        <Box>This device has hit the retry limit and has been flagged for engineering review.</Box>
                        <Button variant="primary" onClick={onStartNewStation}>
                            Start new station
                        </Button>
                    </SpaceBetween>
                </Container>
            );
        }

        return (
            <Container>
                <DrishtiPageHeader
                    title="Calibration failed"
                    description="Follow the remediation steps below and retry."
                />
                <SpaceBetween size="m">
                    <Alert type={isFetchingRemediation ? 'info' : 'warning'} header="Remediation">
                        {isFetchingRemediation ? (
                            <SpaceBetween direction="horizontal" size="s">
                                <Spinner />
                                <Box>Fetching remediation steps…</Box>
                            </SpaceBetween>
                        ) : remediation.length > 0 ? (
                            <SpaceBetween size="xs">
                                {remediation.map((item, idx) => (
                                    <Box key={idx}>{item.instruction}</Box>
                                ))}
                            </SpaceBetween>
                        ) : (
                            // Fires for a FAILED response with an empty list, and for a resumed
                            // failure whose remediation poll did not come back.
                            <Box>Retry or contact engineering.</Box>
                        )}
                    </Alert>
                    {/* Retrying before the steps arrive throws away the run they might have rescued. */}
                    <Button variant="primary" onClick={onRetry} disabled={isFetchingRemediation}>
                        Retry
                    </Button>
                </SpaceBetween>
            </Container>
        );
    }

    // Defensive fallback: TS enum is exhaustive; if this renders, something is wrong.
    return (
        <Container>
            <DrishtiPageHeader title="Calibration" description={`Unknown status: ${status}.`} />
        </Container>
    );
};

export default DrishtiCalibration;
